<template>
  <div id="app">
    <router-view/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { applyBrandTheme } from '@/utils/branding'

export default {
  name: 'App',
  computed: {
    ...mapGetters(['theme', 'company']),
    uiStyle () {
      return (this.company && this.company.uiStyle) || 'classic'
    }
  },
  watch: {
    theme: {
      immediate: true,
      handler (val) {
        document.documentElement.setAttribute('data-bs-theme', val)
      }
    },
    uiStyle: {
      immediate: true,
      handler (val) {
        document.documentElement.setAttribute('data-ui-style', val)
      }
    },
    company: {
      immediate: true,
      deep: true,
      handler (val) {
        applyBrandTheme(val)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/styles/variables';

// ─── Buttons ──────────────────────────────────────────────────────────────────
// Colours come from the corporate-design profile (GET /api/company → CSS
// custom properties set in utils/branding.js). The SCSS values from
// _variables.scss stay as fallback until the company data has loaded.

// ─── Primary button ───────────────────────────────────────────────────────────
.btn.btn-primary {
  background-color: var(--cd-signature, $app-primary);
  border-color: var(--cd-signature, $app-primary);
  color: #fff;

  &:hover, &:focus {
    background-color: var(--cd-signature-hover, $app-primary-hover);
    border-color: var(--cd-signature-hover, $app-primary-hover);
    color: #fff;
    box-shadow: 0 0 0 0.25rem var(--cd-signature-shadow, rgba($app-primary, 0.4));
  }

  &:active, &.active {
    background-color: var(--cd-signature-active, $app-primary-active);
    border-color: var(--cd-signature-active, $app-primary-active);
    color: #fff;
  }

  &:disabled, &.disabled {
    background-color: var(--cd-signature, $app-primary);
    border-color: var(--cd-signature, $app-primary);
    opacity: 0.65;
  }
}

// ─── Info button ─────────────────────────────────────────────────────────────
.btn.btn-info {
  background-color: var(--cd-signature, $app-info);
  border-color: var(--cd-signature, $app-info);
  color: #fff;

  &:hover, &:focus {
    background-color: var(--cd-signature-hover, $app-info-hover);
    border-color: var(--cd-signature-hover, $app-info-hover);
    color: #fff;
    box-shadow: 0 0 0 0.25rem var(--cd-signature-shadow, rgba($app-info, 0.4));
  }

  &:active, &.active {
    background-color: var(--cd-signature-active, $app-info-active);
    border-color: var(--cd-signature-active, $app-info-active);
    color: #fff;
  }

  &:disabled, &.disabled {
    background-color: var(--cd-signature, $app-info);
    border-color: var(--cd-signature, $app-info);
    opacity: 0.65;
  }
}

// ─── Outline-info button ──────────────────────────────────────────────────────
.btn.btn-outline-info {
  color: var(--cd-signature, $app-outline-info);
  border-color: var(--cd-signature, $app-outline-info);
  background-color: transparent;

  &:hover, &:focus {
    background-color: var(--cd-signature, $app-outline-info);
    border-color: var(--cd-signature, $app-outline-info);
    color: #fff;
    box-shadow: 0 0 0 0.25rem var(--cd-signature-shadow, rgba($app-outline-info, 0.4));
  }

  &:active, &.active {
    background-color: var(--cd-signature-active, $app-outline-info-active);
    border-color: var(--cd-signature-active, $app-outline-info-active);
    color: #fff;
  }

  &:disabled, &.disabled {
    color: var(--cd-signature, $app-outline-info);
    border-color: var(--cd-signature, $app-outline-info);
    opacity: 0.65;
  }
}

html {
  font-size: 14px;
}

// CD typography: Poppins for every brand (loaded via @fontsource in main.js)
body {
  --bs-body-font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-family: var(--bs-body-font-family);
}

.btn-toolbar {
  background: none;
}

.pointer {
  cursor: pointer;
  &:hover {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25) !important;
    filter: brightness(0.94) !important;
  }
}

.public.container {
  max-width: 1000px;
}

input.form-control:focus,
textarea.form-control:focus {
  border: 1px solid var(--cd-accent, #377EF6) !important;
}

.public {
  .form {
    margin-top: 50px;
    width: 320px;

    .form-group {
      label {
        font-weight: bold;
        color: #555;
      }

      .error {
        line-height: 1;
        margin-top: 5px;
      }
    }
  }
}

.is-loading-section {
  color: blue;
  margin-top: 1em;
  margin-bottom: 1em;
}

.modal {
  .modal-dialog {
    -webkit-transform: translate(0,-25%);
    -o-transform: translate(0,-25%);
    transform: translate(0,-25%);
    top: 25%;
    margin: 0 auto;
    width: 600px;
    max-width: calc(100vw - 24px);

    .modal-header {
      border-bottom: none;
      padding: 1rem 1rem .5rem;

      .modal-title {
        font-size: 1rem;
      }
    }

    .modal-body {
      padding-bottom: 0;

      textarea {
        resize: none;
        height: 100px;
      }
    }

    .modal-footer {
      justify-content: start;
      border-top: none;
      padding-top: 0;
      padding-bottom: 1.5rem;
    }
  }

  .modified-and-user {
    color: rgb(50, 50, 50);
  }

  .form {
    margin-top: 0px;
    width: 100%;
  }

  .header {
    color: white;
    background-color: rgb(99, 99, 99);
  }
}

[data-bs-theme="dark"] {
  input.form-control:focus,
  textarea.form-control:focus {
    border: 1px solid #5a9cf8 !important;
  }

  .public .form {
    background-color: #2b2b2b;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);

    .form-group label {
      color: #adb5bd;
    }
  }

  .page-header {
    border-bottom-color: #555;

    .header-btn, .logo, .home-icon {
      color: #adb5bd !important;

      &:hover, &:focus {
        color: #dee2e6 !important;
      }
    }
  }

  .footer {
    color: #adb5bd;
    border-top-color: #555;

    a, .theme-toggle {
      color: #adb5bd;

      &:hover {
        color: #dee2e6;
      }
    }
  }

  .info-popup {
    background: #2b2b2b;
    color: #dee2e6;
  }

}

// Classic UI style only: dark theme keeps the severity gradient as
// full-card background, so title text has to be forced dark to stay
// readable against the bright yellow / green / red fills. Subtle mode
// flattens the cards to var(--bs-body-bg) and wants the title text to
// follow var(--bs-body-color) instead, so we exclude it here.
[data-bs-theme="dark"]:not([data-ui-style="subtle"]) {
  .facility-header {
    box-shadow: 0 1px 0 #444 !important;
    color: #000 !important;
  }

  .device-item {
    box-shadow: 0 1px 0 #444 !important;
  }

  .device-headline,
  .sensor-item-headline,
  .device-time {
    color: #000 !important;
  }
}

// Subtle UI style — page background sits a notch darker than pure
// var(--bs-body-bg) so the white-ish cards visibly stand out as the
// content surface. Dark theme deepens the page bg even further so
// the slightly-lighter card surface gets the same "raised" feel.
[data-ui-style="subtle"] {
  body {
    background: #f3f3f5;
  }
}
[data-ui-style="subtle"][data-bs-theme="dark"] {
  body {
    background: #181a1d;
  }
}

// Subtle UI style in dark theme: every PNG icon inside a card header
// (facility / device / sensor type markers, alert bubbles, slashes,
// battery glyphs, the Location button image, ...) is SF-Symbols-style
// line-art — dark silhouette on transparent background. On the
// neutral dark card bg they vanish, so we invert them all. Scoped
// to the card classes so .facility-image (the building photo
// elsewhere in the page) and any future colour assets stay untouched.
[data-bs-theme="dark"][data-ui-style="subtle"] {
  .facility-header img,
  .device-item img,
  .sensor-card img {
    filter: invert(0.85);
  }
}
</style>
